import { useQueryClient } from '@tanstack/react-query';
import type { FormInstance } from 'antd';
import { Form } from 'antd';
import { Content } from 'antd/es/layout/layout';
import React, { useCallback, useMemo, useState } from 'react';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { Mode } from 'app/components/commons/Hotel/Types';
import { hotelPath, useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useSaveHotelFinance } from 'app/hooks/data/hotels/useHotelFinance';
import { BillingInterval } from 'app/typings/billingIntervals';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import './Finance.scss';
import { BankAccountSection } from './_components/BankAccountSection';
import { InvoicingContactSection } from './_components/InvoicingContactSection';
import { InvoicingFrequencySection } from './_components/InvoicingFrequencySection';
import { InvoicingInformationSection } from './_components/InvoicingInformationSection';
import { PaymentMethodSection } from './_components/PaymentMethodSection';
import { StripeSection } from './_components/StripeSection';
import type { FinanceFormData } from './type';

type FinanceSectionProps = {
  isEditMode: boolean;
  hotelId: number;
  form?: FormInstance<FinanceFormData>;
};

type FinanceSection = {
  key: string;
  title: string;
  component: React.ComponentType<FinanceSectionProps>;
};

const FINANCE_SECTIONS: FinanceSection[] = [
  {
    key: 'payment-method',
    title: 'Payment method',
    component: PaymentMethodSection,
  },
  {
    key: 'bank-account',
    title: 'Bank account',
    component: BankAccountSection,
  },
  {
    key: 'invoicing-contact',
    title: 'Invoicing contact',
    component: InvoicingContactSection,
  },
  {
    key: 'invoicing-information',
    title: 'Invoicing information',
    component: InvoicingInformationSection,
  },
  {
    key: 'stripe',
    title: 'Stripe onboarding',
    component: StripeSection,
  },
  {
    key: 'invoicing-frequency',
    title: 'Invoicing frequency',
    component: InvoicingFrequencySection,
  },
];

type FinancialProps = {
  headerMenu: React.ReactNode;
  headerExtra?: React.ReactNode[];
  hotelId: number;
};

export const Finance = ({
  headerMenu,
  headerExtra,
  hotelId,
}: FinancialProps) => {
  const [form] = Form.useForm<FinanceFormData>();
  const queryClient = useQueryClient();

  const { data: hotel, isFetching: isFetchHotelLoading } = useGetHotel(hotelId);

  const { mutateAsync: saveHotelFinance } = useSaveHotelFinance(
    hotelId.toString()
  );

  const [mode, setMode] = useState<Mode>(Mode.View);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = {
    bank: hotel?.finance?.bank,
    iban: hotel?.finance?.iban,
    paymentMethod: hotel?.issuingActivated ? 'vcc' : 'payout',
    invoicingContact: {
      gender: hotel?.finance?.invoicing.gender,
      firstName: hotel?.finance?.invoicing.firstName,
      lastName: hotel?.finance?.invoicing.lastName,
      email: hotel?.finance?.invoicing.email,
    },
    invoicingInformation: {
      companyName: hotel?.finance?.invoicing.hotelName,
      street: hotel?.finance?.invoicing.street,
      zipCode: hotel?.finance?.invoicing.zipCode,
      city: hotel?.finance?.invoicing.city,
      country: hotel?.finance?.invoicing.country,
      vat: hotel?.finance?.vat,
    },
    siret: hotel?.siret,
    eInvoicingId: hotel?.eInvoicingId,
    billingInterval:
      hotel?.issuingActivated || !hotel?.billingInterval
        ? BillingInterval.MONTHLY
        : hotel.billingInterval,
    onBoarding: hotel?.pspOnboardingDone ?? false,
    paymentReady: hotel?.paymentReady ?? false,
  };

  const onFinish = useCallback(
    async (values: FinanceFormData) => {
      setIsLoading(true);

      try {
        const formattedData = {
          issuingActivated: values.paymentMethod === 'vcc' ? true : false,
          finance: {
            bank: values.bank,
            iban: values.iban,
            vat: values.invoicingInformation?.vat,
            invoicing: {
              hotelName: values.invoicingInformation?.companyName,
              gender: values.invoicingContact?.gender,
              firstName: values.invoicingContact?.firstName,
              lastName: values.invoicingContact?.lastName,
              email: values.invoicingContact?.email,
              city: values.invoicingInformation?.city,
              zipCode: values.invoicingInformation?.zipCode,
              street: values.invoicingInformation?.street,
              country: values.invoicingInformation?.country,
            },
          },
          billingInterval: values.billingInterval,
          siret: values.siret,
          eInvoicingId: values.eInvoicingId,
        };

        await saveHotelFinance(formattedData);
        queryClient.invalidateQueries({ queryKey: [hotelPath, { hotelId }] });
        setMode(Mode.View);
      } finally {
        setIsLoading(false);
      }
    },
    [saveHotelFinance, queryClient, hotelId]
  );

  const onFinishFailed = useCallback(() => {
    setIsLoading(false);
  }, []);

  const onSave = useCallback(() => {
    setIsLoading(true);
    form.submit();
  }, [form]);

  const onCancel = useCallback(() => {
    setMode(Mode.View);
  }, []);

  const saveDisabled = useMemo(
    () =>
      mode !== 'view' &&
      form.getFieldsError().every((item) => item.errors.length === 0) &&
      !form.isFieldsTouched(),
    [form, mode]
  );

  if (!hotel || isFetchHotelLoading) {
    return null;
  }

  return (
    <SDetailLayout
      title={
        <AssetDetailTitle
          assetTitle={hotel?.name ?? `Hôtel #${hotelId}`}
          hotelName={hotel?.name}
          isNewDataModel={!hotel?.isLegacy}
        />
      }
      backRoute="/hotels"
      mode={mode}
      onEdit={() => setMode(Mode.Edit)}
      onSave={onSave}
      onCancel={onCancel}
      reset={() => {
        /* no-op */
      }}
      headerMenu={headerMenu}
      headerExtra={headerExtra}
      isDirty={saveDisabled}
      isValid={form.getFieldsError().every((item) => item.errors.length > 0)}
      disabled={isLoading}
      saveDisabled={isLoading}
      isLoading={isLoading}
    >
      <Content className="hotel-detail__content">
        <CurrencyWrapper hotel={hotel}>
          <Form
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            disabled={mode === Mode.View}
            initialValues={initialValues}
            scrollToFirstError={SCROLL_TO_FIRST_ERROR}
          >
            {FINANCE_SECTIONS.map((section) => {
              const SectionComponent = section.component;

              return (
                <SCard
                  key={section.key}
                  title={section.title}
                  className="financial-section"
                >
                  <SectionComponent
                    isEditMode={mode === Mode.Edit}
                    hotelId={hotelId}
                    form={form}
                  />
                </SCard>
              );
            })}
          </Form>
        </CurrencyWrapper>
      </Content>
    </SDetailLayout>
  );
};
